import { useEffect, useRef, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL;

export const useRealTimeUpdates = (onUpdate, userId) => {
  const [connected, setConnected] = useState(false);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);

  const connect = useCallback(() => {
    // Don't connect if no userId
    if (!userId) {
      console.warn('Cannot establish SSE connection: userId is required');
      return;
    }

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const eventSource = new EventSource(`${API_URL}/api/events?userId=${userId}`);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setConnected(true);
        setReconnectAttempts(0);
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type !== 'connected' && onUpdate) {
            onUpdate(data);
          }
        } catch (error) {
          // Error parsing SSE message
        }
      };

      eventSource.onerror = (error) => {
        setConnected(false);
        eventSource.close();

        // Implement exponential backoff for reconnection
        const backoffDelay = Math.min(1000 * Math.pow(2, reconnectAttempts), 30000);
        
        reconnectTimeoutRef.current = setTimeout(() => {
          setReconnectAttempts(prev => prev + 1);
          connect();
        }, backoffDelay);
      };

    } catch (error) {
      setConnected(false);
    }
  }, [onUpdate, reconnectAttempts, userId]);

  useEffect(() => {
    connect();

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [connect]);

  // Manual reconnect function
  const reconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    setReconnectAttempts(0);
    connect();
  };

  return {
    connected,
    reconnect,
    reconnectAttempts
  };
};

export default useRealTimeUpdates;